// FILE: apps/admin/src/components/VoucherPoolModal.tsx
// VERSION: 1.0.0
// START_MODULE_CONTRACT
//   PURPOSE: Admin modal and batch PIN parser for creating LOCAL_STOCK SKUs (e.g. Apple Russia RUB cards) and uploading AES-256-GCM encrypted voucher code batches.
//   SCOPE: ParsedVoucherBatch, parseVoucherCodesBatch, VoucherPoolModalTarget, and VoucherPoolModal component.
//   DEPENDS: ../api.js, ../toast.js
//   LINKS: M-ADMIN
//   ROLE: RUNTIME
//   MAP_MODE: EXPORTS
// END_MODULE_CONTRACT
//
// START_MODULE_MAP
//   ParsedVoucherBatch - Deduplicated multi-line PIN batch parse result with duplicate and skipped counters
//   VoucherPoolModal - Modal dialog for uploading batch voucher PINs into LOCAL_STOCK offers or creating a new LOCAL_STOCK denomination SKU
//   VoucherPoolModalTarget - Discriminated union selecting either PIN upload for an existing offer or creation of a new LOCAL_STOCK SKU
//   parseVoucherCodesBatch - Pure helper trimming, filtering, and deduplicating pasted multi-line PIN codes
// END_MODULE_MAP

import {
  type AdminCatalogOfferDto,
  type AdminCatalogProductDto,
  type AdminCreateLocalOfferRequest,
  type AdminUploadVouchersResponse,
} from '@brainrotstore/api-contracts';
import { useState } from 'react';
import { createPortal } from 'react-dom';

import { api, num } from '../api.js';
import { useToast } from '../toast.js';

export interface ParsedVoucherBatch {
  codes: string[];
  duplicateInBatchCount: number;
  skippedShortCount: number;
}

// START_CONTRACT: parseVoucherCodesBatch
//   PURPOSE: Trim, filter blank/invalid lines, and deduplicate a multi-line pasted voucher PIN list.
//   INPUTS: raw: string
//   OUTPUTS: ParsedVoucherBatch
//   SIDE_EFFECTS: none
//   LINKS: M-ADMIN
// END_CONTRACT: parseVoucherCodesBatch
export function parseVoucherCodesBatch(raw: string): ParsedVoucherBatch {
  // START_BLOCK_PARSE_VOUCHER_CODES_BATCH
  const lines = raw.split(/\r?\n/);
  const seen = new Set<string>();
  const codes: string[] = [];
  let duplicateInBatchCount = 0;
  let skippedShortCount = 0;

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (trimmed.length < 2) {
      skippedShortCount += 1;
      continue;
    }
    if (seen.has(trimmed)) {
      duplicateInBatchCount += 1;
      continue;
    }
    seen.add(trimmed);
    codes.push(trimmed);
  }

  return {
    codes,
    duplicateInBatchCount,
    skippedShortCount,
  };
  // END_BLOCK_PARSE_VOUCHER_CODES_BATCH
}

export type VoucherPoolModalTarget =
  | {
      mode: 'upload';
      product: AdminCatalogProductDto;
      offer: AdminCatalogOfferDto;
    }
  | {
      mode: 'create';
    };

// START_CONTRACT: VoucherPoolModal
//   PURPOSE: Render the operator modal for creating LOCAL_STOCK denominations and uploading batch PIN codes into the encrypted voucher pool.
//   INPUTS: target: VoucherPoolModalTarget, usdRub: number, onClose: () => void, onCompleted: () => void
//   OUTPUTS: React element
//   SIDE_EFFECTS: Calls POST /admin/catalog/local-offers, POST /admin/catalog/vouchers/upload, and PUT /admin/catalog/offers/:id
//   LINKS: M-ADMIN
// END_CONTRACT: VoucherPoolModal
export function VoucherPoolModal({
  target,
  usdRub,
  onClose,
  onCompleted,
}: {
  target: VoucherPoolModalTarget;
  usdRub: number;
  onClose: () => void;
  onCompleted: () => void;
}) {
  // START_BLOCK_VOUCHER_POOL_MODAL
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);

  // Create-mode fields
  const [categorySlug, setCategorySlug] =
    useState<AdminCreateLocalOfferRequest['categorySlug']>('services');
  const [brandKey, setBrandKey] = useState('apple');
  const [countryIso, setCountryIso] = useState('RU');
  const [productTitleRu, setProductTitleRu] = useState('Apple ID (Россия)');
  const [productTitleEn, setProductTitleEn] = useState('Apple Gift Card Russia');
  const [offerTitleRu, setOfferTitleRu] = useState('1 000 ₽');
  const [offerTitleEn, setOfferTitleEn] = useState('1000 RUB');
  const [instructionsRu, setInstructionsRu] = useState(
    'Откройте App Store → профиль → «Погасить подарочную карту или код» и вставьте полученный код.',
  );

  // Shared fields
  const [wholesaleUsdInput, setWholesaleUsdInput] = useState(
    target.mode === 'upload' ? String(target.offer.wholesaleUsd) : '11.5',
  );
  const [rawBatch, setRawBatch] = useState('');
  const [autoPublishAfterUpload, setAutoPublishAfterUpload] = useState(true);

  const parsedBatch = parseVoucherCodesBatch(rawBatch);
  const parsedWholesaleUsd = Number(wholesaleUsdInput.replace(',', '.'));
  const validWholesale = Number.isFinite(parsedWholesaleUsd) && parsedWholesaleUsd > 0;
  const approxCostRub = validWholesale ? Math.round(parsedWholesaleUsd * usdRub) : 0;

  const handleSubmit = async () => {
    if (submitting) return;
    if (!validWholesale) {
      toast('Укажите корректную себестоимость в USD (например, 11.5)');
      return;
    }

    setSubmitting(true);
    try {
      let targetOfferId: string;
      let existingOfferIds = new Set<string>();

      if (target.mode === 'create') {
        if (!brandKey.trim() || !productTitleRu.trim() || !offerTitleRu.trim()) {
          toast('Заполните бренд, название карточки и номинал SKU');
          setSubmitting(false);
          return;
        }
        const createdProduct = await api.post<AdminCatalogProductDto>(
          '/admin/catalog/local-offers',
          {
            categorySlug,
            brandKey: brandKey.trim().toLowerCase(),
            countryIso: countryIso.trim().toUpperCase() || 'RU',
            productTitleRu: productTitleRu.trim(),
            productTitleEn: productTitleEn.trim() || productTitleRu.trim(),
            offerTitleRu: offerTitleRu.trim(),
            offerTitleEn: offerTitleEn.trim() || offerTitleRu.trim(),
            wholesaleUsd: parsedWholesaleUsd,
            instructionsRu: instructionsRu.trim() || null,
            isHot: true,
          },
        );
        const matchingOffer =
          createdProduct.offers.find(
            (o) => o.titleRu.trim() === offerTitleRu.trim(),
          ) ?? createdProduct.offers[createdProduct.offers.length - 1];
        if (!matchingOffer) {
          toast('Локальный товар создан, но не удалось определить SKU');
          onCompleted();
          onClose();
          return;
        }
        targetOfferId = matchingOffer.id;
        existingOfferIds = new Set(createdProduct.offers.map((o) => o.id));
        void existingOfferIds;

        if (parsedBatch.codes.length === 0) {
          toast(`Создан локальный SKU «${matchingOffer.titleRu}». Теперь можно загрузить ПИН-коды.`);
          onCompleted();
          onClose();
          return;
        }
      } else {
        targetOfferId = target.offer.id;
        if (parsedBatch.codes.length === 0) {
          toast('Вставьте хотя бы 1 ПИН-код (по одному коду на строку)');
          setSubmitting(false);
          return;
        }
      }

      const uploadRes = await api.post<AdminUploadVouchersResponse>(
        '/admin/catalog/vouchers/upload',
        {
          offerId: targetOfferId,
          codes: parsedBatch.codes,
          wholesaleUsd: parsedWholesaleUsd,
        },
      );

      if (autoPublishAfterUpload && uploadRes.availableCount > 0) {
        await api.put(`/admin/catalog/offers/${targetOfferId}`, {
          isPublished: true,
        });
      }

      toast(
        `Загружено в пул: +${num(uploadRes.insertedCount)} ПИН-кодов` +
          (uploadRes.duplicateCount > 0
            ? ` (пропущено дублей: ${num(uploadRes.duplicateCount)})`
            : '') +
          ` · Доступно: ${num(uploadRes.availableCount)} шт.`,
      );
      onCompleted();
      onClose();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Ошибка загрузки ПИН-кодов');
    } finally {
      setSubmitting(false);
    }
  };

  const modalNode = (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card"
        style={{ maxWidth: 'min(660px, 100%)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12, gap: 8 }}>
          <div>
            <h2 style={{ margin: 0 }}>
              {target.mode === 'upload'
                ? `Автосклад ПИН-кодов: ${target.product.titleRu} · ${target.offer.titleRu}`
                : 'Новый локальный SKU (Свой склад / РФ)'}
            </h2>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Коды шифруются алгоритмом AES-256-GCM и выдаются автоматически за &lt;50 мс через{' '}
              <code>FOR UPDATE SKIP LOCKED</code>
            </div>
          </div>
          {target.mode === 'upload' && (
            <span className="chip info mono" style={{ fontSize: 12 }}>
              В наличии: {num(target.offer.availableStockCount ?? 0)} шт.
            </span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {target.mode === 'create' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 110px', gap: 10 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                  <span className="muted">Категория</span>
                  <select
                    value={categorySlug}
                    onChange={(e) =>
                      setCategorySlug(
                        e.target.value as AdminCreateLocalOfferRequest['categorySlug'],
                      )
                    }
                  >
                    <option value="services">Сервисы и гифт-карты</option>
                    <option value="steam">Steam</option>
                    <option value="games">Игры</option>
                  </select>
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                  <span className="muted">Ключ бренда (Brand Hub)</span>
                  <input
                    value={brandKey}
                    onChange={(e) => setBrandKey(e.target.value)}
                    placeholder="apple / steam / playstation"
                  />
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                  <span className="muted">Страна ISO</span>
                  <input
                    value={countryIso}
                    onChange={(e) => setCountryIso(e.target.value.toUpperCase())}
                    placeholder="RU"
                  />
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                  <span className="muted">Название региональной ветки (RU)</span>
                  <input
                    value={productTitleRu}
                    onChange={(e) => setProductTitleRu(e.target.value)}
                    placeholder="Apple ID (Россия)"
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                  <span className="muted">Название ветки (EN)</span>
                  <input
                    value={productTitleEn}
                    onChange={(e) => setProductTitleEn(e.target.value)}
                    placeholder="Apple Gift Card Russia"
                  />
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                  <span className="muted">Номинал карты (RU)</span>
                  <input
                    value={offerTitleRu}
                    onChange={(e) => setOfferTitleRu(e.target.value)}
                    placeholder="1 000 ₽"
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                  <span className="muted">Номинал карты (EN)</span>
                  <input
                    value={offerTitleEn}
                    onChange={(e) => setOfferTitleEn(e.target.value)}
                    placeholder="1000 RUB"
                  />
                </label>
              </div>

              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
                <span className="muted">Инструкция по активации (RU)</span>
                <textarea
                  rows={2}
                  value={instructionsRu}
                  onChange={(e) => setInstructionsRu(e.target.value)}
                />
              </label>
            </>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
              alignItems: 'end',
            }}
          >
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
              <span className="muted">
                Себестоимость 1 кода в USD {approxCostRub > 0 ? `(~${approxCostRub} ₽)` : ''}
              </span>
              <input
                value={wholesaleUsdInput}
                onChange={(e) => setWholesaleUsdInput(e.target.value)}
                placeholder="11.5"
              />
            </label>

            <label
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 12.5,
                paddingBottom: 8,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={autoPublishAfterUpload}
                onChange={(e) => setAutoPublishAfterUpload(e.target.checked)}
              />
              <span>Сразу включить SKU и бренд на витрине</span>
            </label>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12.5 }}>
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="muted">
                Список ПИН-кодов (по 1 коду на строку
                {target.mode === 'create' ? ', можно загрузить позже' : ''})
              </span>
              <span className="mono" style={{ fontSize: 11.5 }}>
                К загрузке: <strong>{num(parsedBatch.codes.length)}</strong>
                {parsedBatch.duplicateInBatchCount > 0
                  ? ` · дублей в списке: ${num(parsedBatch.duplicateInBatchCount)}`
                  : ''}
              </span>
            </div>
            <textarea
              rows={7}
              className="mono"
              placeholder={'XXXX-YYYY-ZZZZ-1111\nXXXX-YYYY-ZZZZ-2222'}
              value={rawBatch}
              onChange={(e) => setRawBatch(e.target.value)}
            />
          </label>
        </div>

        <div className="row" style={{ justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button type="button" className="btn ghost" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={submitting}
            onClick={() => void handleSubmit()}
          >
            {submitting
              ? 'Шифрование и сохранение…'
              : target.mode === 'upload'
                ? `Зашифровать и загрузить (${num(parsedBatch.codes.length)})`
                : parsedBatch.codes.length > 0
                  ? `Создать SKU и загрузить (${num(parsedBatch.codes.length)})`
                  : 'Создать локальный SKU'}
          </button>
        </div>
      </div>
    </div>
  );

  if (typeof document !== 'undefined') {
    return createPortal(modalNode, document.body);
  }
  return modalNode;
  // END_BLOCK_VOUCHER_POOL_MODAL
}
